import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Assessment as ReportIcon,
  TrendingDown as GapIcon,
  School as TrainingIcon,
  Timeline as TrendIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import reportsService from '../../services/reportsService';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import { t } from '../../utils/localization/localization';
import { tableShellSx, responsiveTableContainerSx } from '../common';

function ReportsView() {
  const { currentUser, userRole, ROLES } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const domainTableColumnSx = {
    domain: { width: { xs: '30%', md: '28%' }, minWidth: { xs: 180, md: 220 } },
    mastered: { width: { xs: '16%', md: '15%' }, minWidth: { xs: 110, md: 130 }, whiteSpace: 'nowrap' },
    missing: { width: { xs: '16%', md: '15%' }, minWidth: { xs: 110, md: 130 }, whiteSpace: 'nowrap' },
    criticalSkills: { width: { xs: '38%', md: '42%' }, minWidth: { xs: 230, md: 320 } }
  };
  const departmentTableColumnSx = {
    department: { width: { xs: '23%', md: '23%' }, minWidth: { xs: 160, md: 220 } },
    users: { width: { xs: '12%', md: '11%' }, minWidth: { xs: 90, md: 110 }, whiteSpace: 'nowrap' },
    matchRate: { width: { xs: '22%', md: '22%' }, minWidth: { xs: 170, md: 220 } },
    criticalSkill: { width: { xs: '27%', md: '30%' }, minWidth: { xs: 180, md: 280 } },
    missingSkills: { width: { xs: '16%', md: '14%' }, minWidth: { xs: 120, md: 140 }, whiteSpace: 'nowrap' }
  };
  const missingSkillsTableColumnSx = {
    rank: { width: { xs: '8%', md: '8%' }, minWidth: { xs: 60, md: 70 }, whiteSpace: 'nowrap' },
    skill: { width: { xs: '23%', md: '22%' }, minWidth: { xs: 140, md: 220 } },
    employees: { width: { xs: '28%', md: '30%' }, minWidth: { xs: 190, md: 280 } },
    required: { width: { xs: '14%', md: '13%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap' },
    current: { width: { xs: '14%', md: '13%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap' },
    severity: { width: { xs: '13%', md: '14%' }, minWidth: { xs: 100, md: 130 }, whiteSpace: 'nowrap' }
  };
  const trainingTableColumnSx = {
    rank: { width: { xs: '7%', md: '7%' }, minWidth: { xs: 60, md: 70 }, whiteSpace: 'nowrap' },
    skill: { width: { xs: '16%', md: '16%' }, minWidth: { xs: 120, md: 160 } },
    domain: { width: { xs: '14%', md: '14%' }, minWidth: { xs: 110, md: 130 } },
    gap: { width: { xs: '20%', md: '18%' }, minWidth: { xs: 160, md: 220 } },
    expected: { width: { xs: '25%', md: '29%' }, minWidth: { xs: 210, md: 320 } },
    priority: { width: { xs: '10%', md: '8%' }, minWidth: { xs: 90, md: 100 }, whiteSpace: 'nowrap' },
    timeframe: { width: { xs: '8%', md: '8%' }, minWidth: { xs: 90, md: 110 }, whiteSpace: 'nowrap' }
  };

  const loadReport = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      logger.info('ReportsView', 'Loading skills gap report', {
        currentUserId: currentUser.id,
        userRole,
        isAdmin: userRole === ROLES.ADMIN
      });

      let report;
      if (userRole === ROLES.ADMIN) {
        // Admin gets company-wide report
        report = await reportsService.generateCompanyReport();
      } else {
        // Manager gets team report
        report = await reportsService.generateTeamReport(currentUser.id);
      }

      setReportData(report);
      logger.info('ReportsView', 'Skills gap report loaded successfully', {
        employeesAnalyzed: report.general_data?.total_employees_analyzed,
        topGaps: report.top_missing_skills?.length
      });

    } catch (err) {
      logger.error('ReportsView', 'Error loading skills gap report', err);

      // Enhanced error handling for specific error types
      let errorMessage = 'Failed to load skills gap report';
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log out and log back in to refresh your session.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Admin or Manager role required to view reports.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole, ROLES.ADMIN]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownloadPDF = async () => {
    if (!reportData) {
      logger.error('ReportsView', 'Cannot download PDF: No report data available');
      setError('No report data available. Please ensure the report has loaded properly.');
      return;
    }

    setDownloadingPDF(true);
    setError(null); // Clear any previous errors
    try {
      logger.info('ReportsView', 'Starting PDF download process', {
        reportDataKeys: Object.keys(reportData),
        hasGeneralData: !!reportData.general_data,
        hasDomainBreakdown: !!reportData.domain_breakdown,
        hasTopMissingSkills: !!reportData.top_missing_skills
      });

      const filename = `skills-gap-report-${new Date().toISOString().split('T')[0]}.pdf`;
      await reportsService.downloadPDFReport(reportData, filename);

      logger.info('ReportsView', 'PDF report downloaded successfully', { filename });
    } catch (err) {
      logger.error('ReportsView', 'Error downloading PDF report', {
        error: err.message,
        stack: err.stack,
        reportDataExists: !!reportData
      });

      // Enhanced error message for better user feedback
      let errorMessage = 'Failed to download PDF report';
      if (err.message.includes('jsPDF') || err.message.includes('autoTable')) {
        errorMessage = 'PDF generation library error. Please try refreshing the page and trying again.';
      } else if (err.message.includes('import') || err.message.includes('module')) {
        errorMessage = 'PDF library loading error. Please refresh the page and try again.';
      } else {
        errorMessage = err.message || 'Failed to download PDF report';
      }

      setError(errorMessage);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const prepareDomainChartData = () => {
    if (!reportData?.domain_breakdown) return [];

    return Object.entries(reportData.domain_breakdown).map(([domain, data]) => ({
      name: domain,
      mastered: data.mastered_percentage || 0,
      missing: data.missing_percentage || 0
    }));
  };

  const _prepareSkillsRadarData = () => {
    if (!reportData?.domain_breakdown) return [];

    const domains = Object.entries(reportData.domain_breakdown).slice(0, 6); // Top 6 domains
    return domains.map(([domain, data]) => ({
      domain: domain.length > 15 ? domain.substring(0, 15) + '...' : domain,
      mastered: data.mastered_percentage || 0,
      fullMark: 100
    }));
  };

  const prepareTopGapsData = () => {
    if (!reportData?.top_missing_skills) return [];

    return reportData.top_missing_skills.slice(0, 8).map(skill => ({
      name: skill.skill.length > 20 ? skill.skill.substring(0, 20) + '...' : skill.skill,
      percentage: skill.employees_affected_percentage || 0,
      severity: skill.gap_severity || 'Medium'
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Box ml={2}>
          <Typography variant="h6">
            {t('reports.generatingReport')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('reports.analyzingData')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button onClick={loadReport} size="small">
          {t('common.retry')}
        </Button>
      }>
        <Typography variant="subtitle2">{t('reports.errorTitle')}</Typography>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        <Typography variant="subtitle2">{t('reports.noDataTitle')}</Typography>
        {t('reports.noDataMessage')}
      </Alert>
    );
  }

  const domainChartData = prepareDomainChartData();
  const topGapsData = prepareTopGapsData();

  return (
    <Box>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center">
            <ReportIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" gutterBottom>
                📊 {t('reports.skillsGapReport')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {reportData.scope} - {new Date(reportData.generated_date).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reports.preparedBy')}: {reportData.prepared_by}
              </Typography>
            </Box>
          </Box>
          <Tooltip
            title={!reportData ? 'Report data not available. Please refresh and try again.' : ''}
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPDF}
                disabled={downloadingPDF || !reportData}
                sx={{ minWidth: 160 }}
              >
                {downloadingPDF ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('reports.generating')}...
                  </>
                ) : (
                  t('reports.downloadPDF')
                )}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* Section 1: General Data - Key Statistics Cards */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          📊 1. General Data
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <GroupIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="primary" gutterBottom>
                  {reportData.general_data?.total_employees_in_system || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total employees in system
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  ({reportData.general_data?.total_employees_analyzed || 0} with analysis data)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ReportIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="secondary" gutterBottom>
                  {reportData.general_data?.total_job_descriptions_integrated || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Job descriptions integrated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="success" gutterBottom>
                  {reportData.general_data?.total_required_skills || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total required skills
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrainingIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="info" gutterBottom>
                  {reportData.general_data?.total_skills_currently_held || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Skills currently held
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center' }}>
                <GapIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="warning" gutterBottom>
                  {reportData.general_data?.total_missing_skills || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total missing skills
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 2: Key Indicators */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          📈 2. Key Indicators
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {reportData.key_indicators?.average_job_skill_match_rate || 0}%
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Average Job/Skill Match Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="secondary" gutterBottom>
                  {reportData.key_indicators?.min_max_range || '0% / 0%'}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Min/Max Match Rates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main" gutterBottom>
                  {reportData.key_indicators?.average_skills_mastered_per_employee || 0}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Avg Skills Mastered/Employee
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="warning.main" gutterBottom>
                  {reportData.key_indicators?.average_missing_skills_per_employee || 0}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Avg Missing Skills/Employee
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 3: Breakdown by Skill Domain */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          🎯 3. Breakdown by Skill Domain
        </Typography>
        <Grid container spacing={3}>
          {/* Domain Chart */}
          <Grid item xs={12}>
            {domainChartData.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Mastered vs Missing by Domain
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={domainChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={11}
                    />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="mastered" fill="#4caf50" name="Avg % Mastered" />
                    <Bar dataKey="missing" fill="#f44336" name="Avg % Missing" />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Grid>

          {/* Domain Table */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Domain Analysis Summary
            </Typography>
            <Box sx={tableShellSx}>
              <TableContainer sx={responsiveTableContainerSx}>
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: { xs: 760, md: 900 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={domainTableColumnSx.domain}><strong>Domain</strong></TableCell>
                      <TableCell sx={domainTableColumnSx.mastered}><strong>Mastered %</strong></TableCell>
                      <TableCell sx={domainTableColumnSx.missing}><strong>Missing %</strong></TableCell>
                      <TableCell sx={domainTableColumnSx.criticalSkills}><strong>Critical Skills</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(reportData.domain_breakdown || {}).map(([domain, data]) => (
                      <TableRow key={domain}>
                        <TableCell sx={domainTableColumnSx.domain}>{domain}</TableCell>
                        <TableCell sx={domainTableColumnSx.mastered}>
                          <Chip
                            label={`${data.mastered_percentage || 0}%`}
                            color={data.mastered_percentage > 70 ? 'success' : data.mastered_percentage > 40 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={domainTableColumnSx.missing}>
                          <Chip
                            label={`${data.missing_percentage || 0}%`}
                            color={data.missing_percentage < 30 ? 'success' : data.missing_percentage < 60 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell className="table-cell-truncate" sx={{ ...domainTableColumnSx.criticalSkills, fontSize: '0.8rem' }}>
                          {(data.most_critical_skills || []).slice(0, 2).join(', ') || 'None'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 5: Breakdown by Department/Service */}
      {reportData.department_breakdown && Object.keys(reportData.department_breakdown).length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {t('reports.departmentBreakdown')}
          </Typography>
          <Box sx={tableShellSx}>
            <TableContainer sx={responsiveTableContainerSx}>
              <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 860, md: 1020 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={departmentTableColumnSx.department}><strong>{t('reports.departmentService')}</strong></TableCell>
                    <TableCell sx={departmentTableColumnSx.users}><strong>{t('reports.totalUsers')}</strong></TableCell>
                    <TableCell sx={departmentTableColumnSx.matchRate}><strong>{t('reports.avgMatchRate')}</strong></TableCell>
                    <TableCell className="table-cell-truncate" sx={departmentTableColumnSx.criticalSkill}><strong>{t('reports.mostCriticalMissingSkill')}</strong></TableCell>
                    <TableCell sx={departmentTableColumnSx.missingSkills}><strong>{t('reports.totalMissingSkillsColumn')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(reportData.department_breakdown).map(([dept, data]) => (
                    <TableRow key={dept}>
                      <TableCell sx={departmentTableColumnSx.department}><strong>{data.department}</strong></TableCell>
                      <TableCell sx={departmentTableColumnSx.users}>{data.total_users}</TableCell>
                      <TableCell sx={departmentTableColumnSx.matchRate}>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" mr={1}>
                            {data.average_match_rate}%
                          </Typography>
                          <Chip
                            label={
                              data.average_match_rate > 70 ? t('reports.good') :
                                data.average_match_rate > 50 ? t('reports.fair') : t('reports.needsAttention')
                            }
                            color={
                              data.average_match_rate > 70 ? 'success' :
                                data.average_match_rate > 50 ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell className="table-cell-truncate" sx={departmentTableColumnSx.criticalSkill}>{data.most_critical_missing_skill}</TableCell>
                      <TableCell sx={departmentTableColumnSx.missingSkills}>
                        <Chip
                          label={data.total_missing_skills}
                          color={data.total_missing_skills < 5 ? 'success' : data.total_missing_skills < 10 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}

      {/* Top Missing Skills Chart */}
      {topGapsData.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('reports.topMissingSkills')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('reports.percentageEmployeesAffected')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topGapsData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis dataKey="name" type="category" width={150} fontSize={11} />
              <RechartsTooltip />
              <Bar dataKey="percentage" fill="#ff9800" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Top Missing Skills Table */}
      {reportData.top_missing_skills && reportData.top_missing_skills.length > 0 && (
        <Paper elevation={2} sx={{ mb: 4 }}>
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              {t('reports.mostCommonGaps')} ({t('reports.top10')})
            </Typography>
          </Box>
          <Box sx={tableShellSx}>
            <TableContainer sx={responsiveTableContainerSx}>
              <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 860, md: 1020 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={missingSkillsTableColumnSx.rank}><strong>{t('reports.rank')}</strong></TableCell>
                    <TableCell sx={missingSkillsTableColumnSx.skill}><strong>{t('reports.skill')}</strong></TableCell>
                    <TableCell sx={missingSkillsTableColumnSx.employees}><strong>{t('reports.employeesAffected')}</strong></TableCell>
                    <TableCell sx={missingSkillsTableColumnSx.required}><strong>{t('reports.requiredLevel')}</strong></TableCell>
                    <TableCell sx={missingSkillsTableColumnSx.current}><strong>{t('reports.currentLevel')}</strong></TableCell>
                    <TableCell sx={missingSkillsTableColumnSx.severity}><strong>{t('reports.severity')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.top_missing_skills.slice(0, 10).map((skill, index) => (
                    <TableRow key={index}>
                      <TableCell sx={missingSkillsTableColumnSx.rank}>{index + 1}</TableCell>
                      <TableCell sx={missingSkillsTableColumnSx.skill}>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{skill.skill}</Typography>
                      </TableCell>
                      <TableCell sx={missingSkillsTableColumnSx.employees}>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" mr={1}>
                            {skill.employees_affected_percentage}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                          ({skill.employees_affected_count} employees)
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={missingSkillsTableColumnSx.required}>{skill.required_level}</TableCell>
                      <TableCell sx={missingSkillsTableColumnSx.current}>{skill.current_level}</TableCell>
                      <TableCell sx={missingSkillsTableColumnSx.severity}>
                        <Chip
                          label={skill.gap_severity}
                          color={
                            skill.gap_severity === 'High' ? 'error' :
                              skill.gap_severity === 'Medium' ? 'warning' : 'info'
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}

      {/* Section 8: Enhanced Training Recommendations */}
      {reportData.training_recommendations && reportData.training_recommendations.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {t('reports.trainingRecommendationsSection')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            <strong>{t('reports.practicalOutcomesFormat')}</strong>
          </Typography>

          <Box sx={tableShellSx}>
            <TableContainer sx={responsiveTableContainerSx}>
              <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 980, md: 1180 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={trainingTableColumnSx.rank}><strong>{t('reports.rankColumn')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.skill}><strong>{t('reports.skillColumn')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.domain}><strong>{t('reports.domainColumn')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.gap}><strong>{t('reports.gapRequiredVsCurrent')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.expected}><strong>{t('reports.expectedResultsAfterTraining')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.priority}><strong>{t('reports.priorityColumn')}</strong></TableCell>
                    <TableCell sx={trainingTableColumnSx.timeframe}><strong>{t('reports.timeframeColumn')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.training_recommendations.slice(0, 8).map((rec, index) => (
                    <TableRow key={index}>
                      <TableCell sx={trainingTableColumnSx.rank}>
                        <Chip
                          label={rec.rank || (index + 1)}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={trainingTableColumnSx.skill}>
                        <Typography variant="subtitle2" color="primary">
                          {rec.skill}
                        </Typography>
                      </TableCell>
                      <TableCell sx={trainingTableColumnSx.domain}>
                        <Chip
                          label={rec.domain}
                          size="small"
                          color={
                            rec.domain?.toLowerCase().includes('technique') ? 'info' :
                              rec.domain?.toLowerCase().includes('communication') ? 'secondary' :
                                rec.domain?.toLowerCase().includes('leadership') ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell className="table-cell-truncate" sx={{ ...trainingTableColumnSx.gap, fontSize: '0.85rem' }}>
                        {rec.gap}
                      </TableCell>
                      <TableCell className="table-cell-truncate" sx={trainingTableColumnSx.expected}>
                        <Typography variant="body2" color="text.primary">
                          <strong>{t('reports.ableTo')}</strong> {rec.expected_results?.replace('Employees will be able to ', '') || 'Apply skills effectively'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={trainingTableColumnSx.priority}>
                        <Chip
                          label={rec.priority}
                          color={
                            rec.priority === 'Critical' ? 'error' :
                              rec.priority === 'Important' ? 'warning' : 'info'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={trainingTableColumnSx.timeframe}>
                        <Typography variant="caption" color="text.secondary">
                          {rec.timeframe}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Summary Statistics */}
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>{t('reports.trainingPrioritySummary')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card elevation={1} sx={{ p: 2, bgcolor: '#ffebee' }}>
                  <Typography variant="h4" color="error.main" gutterBottom>
                    {reportData.analysis_summary?.high_priority_recommendations || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.critical1To3Months')}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card elevation={1} sx={{ p: 2, bgcolor: '#fff3e0' }}>
                  <Typography variant="h4" color="warning.main" gutterBottom>
                    {reportData.analysis_summary?.medium_priority_recommendations || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.important3To6Months')}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card elevation={1} sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="h4" color="info.main" gutterBottom>
                    {reportData.analysis_summary?.low_priority_recommendations || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.standard6To12Months')}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Section 9: Conclusion & Next Steps */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t('reports.conclusionSection')}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          {/* Key Strengths */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ p: 3, bgcolor: '#e8f5e8', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('reports.keyStrengthsIdentified')}
              </Typography>
              <List dense>
                {reportData.key_strengths && reportData.key_strengths.length > 0 ? (
                  reportData.key_strengths.slice(0, 4).map((strength, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: '20px' }}>
                        <Typography variant="body2" color="success.main">•</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.primary">
                            {strength}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary">
                          {t('reports.analysisTopPerforming')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Card>
          </Grid>

          {/* Critical Weaknesses */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ p: 3, bgcolor: '#ffeaea', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('reports.criticalWeaknessesIdentified')}
              </Typography>
              <List dense>
                {reportData.critical_weaknesses && reportData.critical_weaknesses.length > 0 ? (
                  reportData.critical_weaknesses.slice(0, 4).map((weakness, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: '20px' }}>
                        <Typography variant="body2" color="error.main">•</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.primary">
                            {weakness}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary">
                          {t('reports.noCriticalWeaknessesIdentified')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Card>
          </Grid>

          {/* Priority Training Areas */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ p: 3, bgcolor: '#e3f2fd', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('reports.priorityTrainingAreasSection')}
              </Typography>
              <List dense>
                {reportData.priority_training_areas && reportData.priority_training_areas.length > 0 ? (
                  reportData.priority_training_areas.slice(0, 4).map((area, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: '20px' }}>
                        <Typography variant="body2" color="primary.main">•</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.primary">
                            {area}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary">
                          {t('reports.noPriorityAreasSpecified')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Card>
          </Grid>
        </Grid>

        {/* Recommended Timeline */}
        <Box mt={3}>
          <Card elevation={2} sx={{ p: 3, bgcolor: '#fff3e0' }}>
            <Typography variant="h6" gutterBottom color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('reports.recommendedTimelineSection')}
            </Typography>
            <Typography variant="body1" color="text.primary">
              {reportData.recommended_timeline || t('reports.defaultTimeline')}
            </Typography>
          </Card>
        </Box>

        {/* Analysis Summary */}
        {reportData.analysis_summary && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>{t('reports.analysisSummarySection')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">{t('reports.domainsAnalyzed')}</Typography>
                <Typography variant="h5" color="primary">{reportData.analysis_summary.total_domains_analyzed}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">{t('reports.departmentsCovered')}</Typography>
                <Typography variant="h5" color="secondary">{reportData.analysis_summary.departments_covered}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">{t('reports.criticalRecommendations')}</Typography>
                <Typography variant="h5" color="error">{reportData.analysis_summary.high_priority_recommendations}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">{t('reports.totalRecommendationsLabel')}</Typography>
                <Typography variant="h5" color="info.main">
                  {(reportData.analysis_summary.high_priority_recommendations || 0) +
                   (reportData.analysis_summary.medium_priority_recommendations || 0) +
                   (reportData.analysis_summary.low_priority_recommendations || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ReportsView;
